import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ruleToFilters } from './segmentPresets'
import type { ProductSegmentRule } from './segmentPresets'
import type { ProductTableFilters } from '@/hooks/useProductTableConfig'

interface SegmentLiveCountProps {
  rule: ProductSegmentRule
  /** Must be stable (memoize via useCallback in the caller), else unnecessary timer resets. */
  countFn: (filters: ProductTableFilters) => Promise<number>
  /** Channel-aware count using the RAW rule (bypasses ruleToFilters). When set, countFn is not called. */
  countRuleFn?: (rule: ProductSegmentRule) => number | Promise<number>
}

/** Debounced live product count for the current rule (300ms). */
export function SegmentLiveCount({ rule, countFn, countRuleFn }: SegmentLiveCountProps) {
  const { t } = useTranslation()
  const [count, setCount] = useState<number | null>(null)

  useEffect(() => {
    let ignored = false
    const timer = setTimeout(() => {
      const result = countRuleFn ? countRuleFn(rule) : countFn(ruleToFilters(rule))
      Promise.resolve(result).then((n) => {
        if (!ignored) setCount(n)
      })
    }, 300)
    return () => {
      ignored = true
      clearTimeout(timer)
    }
  }, [rule, countFn, countRuleFn])

  const label = count === null ? '…' : String(count)

  return (
    <span
      className="text-sm text-neutral-500 dark:text-neutral-400"
      aria-live="polite"
    >
      {t('productSegments.builder.countPrefix')}{' '}
      <span data-testid="rule-count">{label}</span>
      {count !== null && ` ${t('productSegments.builder.countSuffix')}`}
    </span>
  )
}

SegmentLiveCount.displayName = 'SegmentLiveCount'
